import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { API_URL } from '../../utils/config';
import {
  FileText, Download, Search, Calendar, Building2,
  CheckCircle, RefreshCw, Loader2, ShieldCheck
} from 'lucide-react';

const RicevuteQuestura = () => {
  const token = localStorage.getItem('hw_token');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterSearch, setFilterSearch] = useState('');
  const [filterPropertyCode, setFilterPropertyCode] = useState('all');

  useEffect(() => {
    fetchReceipts();
  }, [filterYear]);

  const fetchReceipts = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_URL}/api/alloggiati/receipts?`;
      if (filterYear && filterYear !== 'all') url += `year=${filterYear}&`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReceipts(data.receipts || []);
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.detail || 'Errore caricamento ricevute');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  const handleDownload = async (receipt) => {
    try {
      // Prova prima l'endpoint via property_code/year/filename (Google Cloud Storage)
      if (receipt.property_code && receipt.year && receipt.filename) {
        const url = `${API_URL}/api/alloggiati/receipts/${receipt.property_code}/${receipt.year}/${receipt.filename}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          window.open(data.download_url, '_blank');
          return;
        }
      }
      // Fallback: download diretto via id
      if (receipt.id) {
        const url = `${API_URL}/api/alloggiati/receipts/download/${receipt.id}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          return;
        }
      }
      alert('Ricevuta non disponibile per il download');
    } catch (e) {
      alert('Errore di connessione');
    }
  };

  // Lista univoca property_code dalle ricevute
  const propertyCodes = useMemo(() => {
    const set = new Set();
    receipts.forEach((r) => r.property_code && set.add(r.property_code));
    return Array.from(set).sort();
  }, [receipts]);

  const filteredReceipts = useMemo(() => {
    let list = receipts;
    if (filterPropertyCode !== 'all') {
      list = list.filter((r) => r.property_code === filterPropertyCode);
    }
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      list = list.filter((r) =>
        (r.property_name || '').toLowerCase().includes(s) ||
        (r.guest_name || '').toLowerCase().includes(s) ||
        (r.booking_code || '').toLowerCase().includes(s) ||
        (r.property_code || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [receipts, filterPropertyCode, filterSearch]);

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6 p-6" data-testid="ricevute-questura-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-blue-600" />
            Archivio Ricevute Questura
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visualizza e scarica le ricevute di trasmissione AlloggiatiWeb dei tuoi immobili.
          </p>
        </div>
        <Button onClick={fetchReceipts} variant="outline" data-testid="btn-refresh-receipts">
          <RefreshCw className="h-4 w-4 mr-2" /> Aggiorna
        </Button>
      </div>

      {/* Info bar */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4 pb-4">
          <p className="text-sm text-slate-700 flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span>
              Qui trovi tutte le ricevute delle schedine inviate al portale Alloggiati Web (Polizia di Stato)
              per gli immobili da te gestiti. Le ricevute sono il <b>documento legale</b> che prova
              l'avvenuta trasmissione entro le 24 ore di legge.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Filtri */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Anno</label>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Immobile</label>
              <Select value={filterPropertyCode} onValueChange={setFilterPropertyCode}>
                <SelectTrigger data-testid="filter-property-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i miei immobili</SelectItem>
                  {propertyCodes.map((code) => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Cerca (ospite / immobile / cod. pren.)</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Es. Rossi Mario, Casa Mare..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  data-testid="filter-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella ricevute */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Ricevute disponibili</span>
            <Badge variant="outline" data-testid="receipts-count">
              {filteredReceipts.length} ricevut{filteredReceipts.length === 1 ? 'a' : 'e'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento ricevute...
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 bg-red-50 p-4 rounded border border-red-200" data-testid="error-message">
              {error}
            </div>
          ) : filteredReceipts.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nessuna ricevuta trovata per i filtri selezionati.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Data Invio</th>
                    <th className="text-left px-3 py-2">Immobile</th>
                    <th className="text-left px-3 py-2">Ospite</th>
                    <th className="text-left px-3 py-2">Pren.</th>
                    <th className="text-center px-3 py-2">Stato</th>
                    <th className="text-right px-3 py-2">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((r, idx) => (
                    <tr
                      key={r.id || `${r.property_code}_${idx}`}
                      className="border-b last:border-0 hover:bg-slate-50/60"
                      data-testid={`receipt-row-${idx}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          {r.receipt_date || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          <div>
                            <div className="font-medium text-slate-900">{r.property_name || '—'}</div>
                            <div className="text-xs text-slate-500">{r.property_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.guest_name || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.booking_code || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" /> Confermata
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(r)}
                          data-testid={`btn-download-${idx}`}
                        >
                          <Download className="h-3.5 w-3.5 mr-1" /> Scarica PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RicevuteQuestura;
